import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface SelectOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrl: './custom-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true
    }
  ]
})
export class CustomSelectComponent<T = any> implements ControlValueAccessor  {
  @Input() options: SelectOption<T>[] = [];
  @Input() placeholder = 'Select…';
  @Input() panelHeight = 200;
  @Input() defaultValue: T | null = null;
  @Output() change = new EventEmitter<T>();

  @ViewChild('panel') panelRef!: ElementRef<HTMLDivElement>;

  open = false;
  active = -1;
  value!: T | null;


  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(val: T | null): void { this.value = val; }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  get selectedLabel(): string {
    const hit = this.options.find(o => o.value === this.value);
    return hit ? hit.label : this.placeholder;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultValue']) {
      this.value = this.defaultValue;
    }
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      const idx = this.options.findIndex(o => o.value === this.value);
      this.active = idx;
      setTimeout(() => {
        const el = this.panelRef?.nativeElement.querySelector('.option.active');
        el?.scrollIntoView({ block: 'nearest' });
      });
    }
  }

  select(option: SelectOption<T>): void {
    this.value = option.value;
    this.onChange(this.value);
    this.change.emit(this.value);
    this.close();
  }

  close(): void {
    this.open = false;
    this.active = -1;
    this.onTouched();
  }

  @HostListener('keydown', ['$event'])
  handleKey(e: KeyboardEvent): void {
    if (!this.open && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      this.toggle();
      return;
    }
    if (!this.open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.active = (this.active + 1) % this.options.length;
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.active = (this.active - 1 + this.options.length) % this.options.length;
        this.scrollActiveIntoView();
        break;
      case 'Enter':
        e.preventDefault();
        this.select(this.options[this.active]);
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  private scrollActiveIntoView(): void {
    const el = this.panelRef?.nativeElement.querySelector('.option.active');
    el?.scrollIntoView({ block: 'nearest' });
  }

  @HostListener('document:click', ['$event'])
  outside(event: MouseEvent): void {
    if (!this.open) return;
    if (!this.elem.nativeElement.contains(event.target)) this.close();
  }

  constructor(private elem: ElementRef) { }
}
